import React, { useState, useEffect, useCallback } from "react";
import {
  RefreshButtonStyled,
  StationFooterStyled,
} from "../Stations/components/styles";
import { ReactComponent as RefreshIcon } from "../Intersections/assets/refreshIcon.svg";

interface LastUpdatedFooterProps {
  onRefresh: () => Promise<void> | void;
  lastUpdatedAt?: number | null;
  isLoading?: boolean;
}

export const LastUpdatedFooter: React.FC<LastUpdatedFooterProps> = ({
  onRefresh,
  lastUpdatedAt: lastUpdatedAtProp,
  isLoading = false,
}) => {
  const [lastUpdatedAt, setLastUpdatedAt] = useState<number | null>(
    lastUpdatedAtProp ?? Date.now()
  );
  const [timeAgoText, setTimeAgoText] = useState<string>("just now");

  useEffect(() => {
    if (lastUpdatedAtProp) setLastUpdatedAt(lastUpdatedAtProp);
  }, [lastUpdatedAtProp]);

  const getTimeAgoText = useCallback(() => {
    if (!lastUpdatedAt) return "unknown";
    const now = Date.now();
    const diffInMs = now - lastUpdatedAt;
    const diffInSeconds = Math.floor(diffInMs / 1000);
    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));

    if (diffInSeconds < 1) return "just now";
    if (diffInMinutes < 1) return `${diffInSeconds} seconds ago`;
    if (diffInMinutes === 1) return "1 minute ago";
    return `${diffInMinutes} minutes ago`;
  }, [lastUpdatedAt]);

  useEffect(() => {
    if (!lastUpdatedAt) return;
    let timeoutId: number;
    const scheduleUpdate = () => {
      setTimeAgoText(getTimeAgoText());
      const now = Date.now();
      const diffInMs = now - lastUpdatedAt;
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      const nextDelay =
        diffInMinutes < 1
          ? 15000 - (diffInMs % 15000)
          : 60000 - (diffInMs % 60000);
      timeoutId = setTimeout(scheduleUpdate, nextDelay);
    };
    scheduleUpdate();
    return () => clearTimeout(timeoutId);
  }, [lastUpdatedAt, getTimeAgoText]);

  const handleRefresh = async () => {
    await onRefresh();
    setLastUpdatedAt(Date.now());
  };

  return (
    <StationFooterStyled>
      <span>Last updated: {timeAgoText}</span>
      <RefreshButtonStyled onClick={handleRefresh} disabled={isLoading}>
        <RefreshIcon /> Refresh
      </RefreshButtonStyled>
    </StationFooterStyled>
  );
};
